import { useNavigate, useParams } from 'react-router-dom';
import { useReturnTo } from '../../../../hooks/useReturnTo';
import { useForm, useFormState, useFieldArray } from "react-hook-form";
import { useEffect, useState } from "react";

// Types
import IntegrationType from "../../../../types/integration/IntegrationType";

// Constants.
import {
    ACTIONS,
    generalErrorMessage,
} from '../../../../component/Constants';

//Utils
import { getAction, getValidClass } from "../../../../helpers/utils";
import Action from "../../../../types/Action";
import api from "../../../../api/Base";
import Alert from "../../../../component/Alert";
import IntegrationsApi from "../../../../api/Integrations";
import useIntegrationTest from "../../../../hooks/useIntegrationTest";
import {
    findProfile,
    getNextProfilePrefix,
    NOTIFICATION_INTEGRATIONS
} from "../../../../helpers/notificationIntegrations";

interface Header {
    name: string;
    value: string;
}

interface WebhookFormData {
    name: string;
    url: string;
    dbmarurl: string;
    enable: boolean;
    content: string;
    hostContent: string;
    headers: Header[];
}

function Detail(props: { webhooks: IntegrationType[], refresh: Function, isAdmin: boolean }) {
    const { actionId, profileId } = useParams();
    const [action, setAction] = useState<Action>(ACTIONS.CREATE);
    const navigate = useNavigate();
    const config = NOTIFICATION_INTEGRATIONS.webhook;
    // Cancel and a successful save return where the user came from -- the alert-rule
    // form links straight to a profile -- falling back to this integration's list.
    const returnFromForm = useReturnTo(config.path);

    // Redirect non-admin users
    useEffect(() => {
        if (!props.isAdmin) {
            navigate('/admin/integrations/webhooks/');
        }
    }, [props.isAdmin, navigate]);
    
    const isDelete = action.type === ACTIONS.DELETE.type;
    const {
        register,
        trigger,
        handleSubmit,
        getValues,
        setValue,
        control,
        formState: {errors}
    } = useForm<WebhookFormData>({
        mode: "onBlur",
        reValidateMode: 'onSubmit',
        shouldFocusError: true,
        defaultValues: {
            headers: [{ name: '', value: '' }]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "headers"
    });

    useEffect(() => {
        getAction(actionId, setAction, setDisabled);
    }, [actionId]);

    const [disabled, setDisabled] = useState(isDelete);
    const [originalHeaderNames, setOriginalHeaderNames] = useState<Set<string>>(new Set());

    const { TestNowButton, TestResultAlert } = useIntegrationTest({
        onTest: () => {
            const data = getValues();
            const params: { name: string; value: string }[] = [
                { name: 'webhook.url', value: data.url || '' },
                { name: 'webhook.dbmarlin.url', value: data.dbmarurl || '' },
                { name: 'webhook.enable', value: data.enable === true ? 'true' : 'false' },
                { name: 'webhook.content', value: data.content || '' },
                { name: 'webhook.host.content', value: data.hostContent || '' },
            ];
            (data.headers || []).forEach((h: Header) => {
                if (h?.name?.trim() && h?.value?.trim()) {
                    params.push({ name: `webhook.header.${h.name.trim()}`, value: h.value.trim() });
                }
            });
            return IntegrationsApi.testWebhookIntegration(params);
        },
        successMessage: 'Webhook test notification sent successfully.',
        errorMessage: 'Test failed. Please check your webhook settings.',
    });

    const parseEditResponse = (data: any) => {
        if (action.type === ACTIONS.CREATE.type) {
            return;
        }
        const profile = findProfile(data, config, action.type === ACTIONS.CREATE.type ? undefined : profileId);
        if (profile) {
            const prefix = profile.prefix;
            setValue('name', profile.label);
            // Parse URL
            const url = data.find((item: { name: string; }) => item?.name === `${prefix}.url`);
            if (url) setValue('url', url.value);

            // Parse DBmarlin URL
            const dbmarurl = data.find((item: { name: string; }) => item?.name === `${prefix}.dbmarlin.url`);
            if (dbmarurl) setValue('dbmarurl', dbmarurl.value);

            // Parse enable
            const enable = data.find((item: { name: string; }) => item?.name === `${prefix}.enable`);
            if (enable) setValue('enable', enable.value === 'true');

            // Parse content templates
            const content = data.find((item: { name: string; }) => item?.name === `${prefix}.content`);
            if (content) setValue('content', content.value || '');

            const hostContent = data.find((item: { name: string; }) => item?.name === `${prefix}.host.content`);
            if (hostContent) setValue('hostContent', hostContent.value || '');

            // Parse headers (remove webhook.header. prefix)
            const headerPrefix = `${prefix}.header.`;
            const headerParams = data.filter((item: { name: string; }) => item?.name.startsWith(headerPrefix));
            const headers: Header[] = headerParams.map((item: { name: string; value: string; }) => ({
                name: item.name.replace(headerPrefix, ''),
                value: item.value
            }));
            
            // Store original header names for tracking deletions
            setOriginalHeaderNames(new Set(headerParams.map((item: { name: string; }) => item.name)));
            
            if (headers.length > 0) {
                setValue('headers', headers);
            } else {
                setValue('headers', [{ name: '', value: '' }]);
            }

            void trigger();
        }
    }

    useEffect(() => {
        parseEditResponse(props.webhooks);
    }, [props.webhooks, action.type, profileId]);

    const {dirtyFields} = useFormState({
        control
    })

    const onSubmit = async(data: WebhookFormData) => {
        setDisabled(true);
        const updatedValues: { name: string; value: any; }[] = [];
        const deleteValues: { name: string; }[] = [];
        let result: any;
        let url = `/parameter`;
        const prefix = action.type === ACTIONS.CREATE.type
            ? getNextProfilePrefix(props.webhooks, config)
            : profileId || config.basePrefix;

        if (action.type === ACTIONS.DELETE.type) {
            const profile = findProfile(props.webhooks, config, prefix);
            result = await api.delete(url, { data: (profile?.parameters ?? []).map(parameter => ({ name: parameter.name })) });
            if (result.status === 200) {
                returnFromForm();
                props.refresh();
            }
            return;
        }

        if (action.type === 'create' || dirtyFields.name) {
            updatedValues.push({ name: `${prefix}.dbmarlin.name`, value: data.name || '' });
        }

        // Handle URL
        if (action.type === 'create' || dirtyFields.url) {
            updatedValues.push({ name: `${prefix}.url`, value: data.url || '' });
        }

        // Handle DBmarlin URL
        if (action.type === 'create' || dirtyFields.dbmarurl) {
            updatedValues.push({ name: `${prefix}.dbmarlin.url`, value: data.dbmarurl || '' });
        }

        // Handle enable
        if (action.type === 'create' || dirtyFields.enable) {
            updatedValues.push({ name: `${prefix}.enable`, value: data.enable === true ? 'true' : 'false' });
        }

        // Handle content templates
        if (action.type === 'create' || dirtyFields.content) {
            updatedValues.push({ name: `${prefix}.content`, value: data.content || '' });
        }

        if (action.type === 'create' || dirtyFields.hostContent) {
            updatedValues.push({ name: `${prefix}.host.content`, value: data.hostContent || '' });
        }

        // Handle headers
        if (action.type === 'update') {
            // On update: always check for deleted headers and handle additions
            const currentHeaders = getValues('headers') || [];
            const currentHeaderNames = new Set(
                currentHeaders
                    .filter((h: Header) => h?.name?.trim())
                    .map((h: Header) => `${prefix}.header.${h.name.trim()}`)
            );
            
            // Find deleted headers (exist in original but not in current)
            const deletedHeaders = Array.from(originalHeaderNames).filter(
                name => !currentHeaderNames.has(name)
            );
            
            // Add deleted headers to deleteValues
            deletedHeaders.forEach(name => {
                deleteValues.push({ name });
            });
            
            // Add current headers from form
            currentHeaders.forEach((header: Header) => {
                if (header?.name?.trim() && header?.value?.trim()) {
                    updatedValues.push({ 
                        name: `${prefix}.header.${header.name.trim()}`,
                        value: header.value.trim() 
                    });
                }
            });
        } else if (action.type === 'create' || dirtyFields.headers) {
            // Create/Delete: Only add current headers (no individual header deletions)
            const currentHeaders = getValues('headers') || [];
            currentHeaders.forEach((header: Header) => {
                if (header?.name?.trim() && header?.value?.trim()) {
                    updatedValues.push({ 
                        name: `${prefix}.header.${header.name.trim()}`,
                        value: header.value.trim() 
                    });
                }
            });
        }

        // Send DELETE request for deleted items
        if (deleteValues.length > 0) {
            result = await api.delete(url, { data: deleteValues });
        }

        // Send POST request for updated/created items
        if (updatedValues.length > 0) {
            result = await api.post(url, updatedValues);
        } else {
            result = { status: 200 };
        }

        if (result.status === 200) {
            returnFromForm();
            props.refresh();
        }
    }

    const isValid = Object.keys(errors).length === 0;

    return (
        <div className="col">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-webhook fa-fw" aria-hidden="true"/>
                    {action.title} Webhook Integration
                </div>
                <div className="card-body">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {!isValid && (
                            <Alert message={generalErrorMessage.text} heading={generalErrorMessage.title}
                                   variant="alert-danger"/>
                        )}

                        <p>Please complete the required details below:</p>

                        <div className="row g-0 form">
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="name" className="base-url">Name</label>
                                </span>
                            </div>
                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <input
                                    type="text"
                                    id="name"
                                    maxLength={250}
                                    {...register('name', { required: "Please fill in this field" })}
                                    className={`form-control ${getValidClass(errors.name, getValues('name'), isDelete)}`}
                                    defaultValue=''
                                    disabled={disabled}
                                    title={errors.name?.message}
                                    data-lpignore={true}
                                />
                                <small className="text-danger">{errors.name?.message}</small>
                            </div>
                            <div className="w-100"/>

                            {/* URL */}
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="url" className="base-url">Webhook URL</label>
                                </span>
                            </div>

                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <input
                                    type="text"
                                    id="url"
                                    maxLength={250}
                                    {...register('url', {
                                        required: "Please fill in this field",
                                        pattern: {
                                            value: /^(ftp|http|https):\/\/[^ "]+$/,
                                            message: "Invalid Url"
                                        }
                                    })}
                                    className={`form-control ${getValidClass(errors.url, getValues('url'), isDelete)}`}
                                    defaultValue=''
                                    disabled={disabled}
                                    title={errors.url?.message}
                                    data-lpignore={true}
                                />
                                <small className="text-danger">{errors.url?.message}</small>
                            </div>
                            <div className="w-100"/>

                            {/* DBmarlin URL */}
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="dbmarurl" className="base-url">DBmarlin URL</label>
                                </span>
                            </div>

                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <input
                                    type="text"
                                    id="dbmarurl"
                                    maxLength={250}
                                    {...register('dbmarurl', {
                                        required: "Please fill in this field",
                                        pattern: {
                                            value: /^(ftp|http|https):\/\/[^ "]+$/,
                                            message: "Invalid Url"
                                        }
                                    })}
                                    className={`form-control ${getValidClass(errors.dbmarurl, getValues('dbmarurl'), isDelete)}`}
                                    defaultValue=''
                                    disabled={disabled}
                                    title={errors.dbmarurl?.message}
                                    data-lpignore={true}
                                />
                                <small className="text-danger">{errors.dbmarurl?.message}</small>
                            </div>
                            <div className="w-100"/>

                            {/* Headers */}
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="headers">Headers</label>
                                </span>
                            </div>
                            <div className="col-xs-12 col-md-9">
                                <div className="border rounded p-3 mb-3" style={{ backgroundColor: 'var(--dbm-bg-surface-alt)' }}>
                                    {fields.map((field, index) => {
                                    const nameRegister = register(`headers.${index}.name` as const, {
                                        required: "Header name is required",
                                        validate: (value) => {
                                            if (!value || !value.trim()) {
                                                return "Header name cannot be empty";
                                            }
                                            return true;
                                        }
                                    });
                                    const valueRegister = register(`headers.${index}.value` as const, {
                                        required: "Header value is required",
                                        validate: (value) => {
                                            if (!value || !value.trim()) {
                                                return "Header value cannot be empty";
                                            }
                                            return true;
                                        }
                                    });
                                    
                                    return (
                                    <div key={field.id} className="row mb-2">
                                        <div className="col-md-5">
                                            <input
                                                type="text"
                                                placeholder="Header Name"
                                                {...nameRegister}
                                                onBlur={(e) => {
                                                    nameRegister.onBlur(e);
                                                    void trigger(`headers.${index}.name`);
                                                }}
                                                className={`form-control ${getValidClass(errors.headers?.[index]?.name, getValues(`headers.${index}.name`), isDelete)}`}
                                                disabled={disabled}
                                                title={errors.headers?.[index]?.name?.message}
                                                data-lpignore={true}
                                            />
                                            {errors.headers?.[index]?.name && (
                                                <small className="text-danger">{errors.headers[index]?.name?.message}</small>
                                            )}
                                        </div>
                                        <div className="col-md-5">
                                            <input
                                                type="text"
                                                placeholder="Header Value"
                                                {...valueRegister}
                                                onBlur={(e) => {
                                                    valueRegister.onBlur(e);
                                                    void trigger(`headers.${index}.value`);
                                                }}
                                                className={`form-control ${getValidClass(errors.headers?.[index]?.value, getValues(`headers.${index}.value`), isDelete)}`}
                                                disabled={disabled}
                                                title={errors.headers?.[index]?.value?.message}
                                                data-lpignore={true}
                                            />
                                            {errors.headers?.[index]?.value && (
                                                <small className="text-danger">{errors.headers[index]?.value?.message}</small>
                                            )}
                                        </div>
                                        <div className="col-md-2">
                                            {!disabled && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => {
                                                            remove(index);
                                                        }}
                                                    >
                                                        <i className="fal fa-trash-alt"/>
                                                    </button>
                                                    &nbsp;
                                                    {index === fields.length - 1 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => {
                                                                append({ name: '', value: '' }, { shouldFocus: false });
                                                            }}
                                                        >
                                                            <i className="fal fa-plus"/>
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    );
                                })}
                                {fields.length === 0 && !disabled && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-success"
                                        onClick={() => append({ name: '', value: '' }, { shouldFocus: false })}
                                    >
                                        <i className="fal fa-plus"/> Add Header
                                    </button>
                                )}
                                </div>
                            </div>
                            <div className="w-100"/>

                            {/* Content Template */}
                            <div className="col-xs-12 col-md-3">
                                <label htmlFor="contentTemplate">Content Template</label>
                            </div>
                            <div className="col-xs-12 col-md-9">
                                <textarea
                                    id="contentTemplate"
                                    rows={6}
                                    {...register('content', {
                                        validate: (value) => {
                                            if (!value || value.trim() === '') {
                                                return true; // Allow empty, only validate format if provided
                                            }
                                            try {
                                                JSON.parse(value);
                                                return true;
                                            } catch (e) {
                                                return "Invalid JSON format";
                                            }
                                        }
                                    })}
                                    className={`form-control ${getValidClass(errors.content, getValues('content'), isDelete)}`}
                                    style={{ resize: 'vertical', minHeight: '200px' }}
                                    disabled={disabled}
                                    placeholder='{ "body": { "instance": "<datasourcename>", ... } }'
                                    title={errors.content?.message}
                                    data-lpignore={true}
                                />
                                <small className="text-danger">{errors.content?.message}</small>
                                <small className="text-muted d-block">Use substitution variables like &lt;datasourcename&gt; etc.</small>
                            </div>
                            <div className="w-100"/>

                            {/* Host Content Template */}
                            <div className="col-xs-12 col-md-3">
                                <label htmlFor="hostContentTemplate">Host Content Template</label>
                            </div>
                            <div className="col-xs-12 col-md-9">
                                <textarea
                                    id="hostContentTemplate"
                                    rows={6}
                                    {...register('hostContent', {
                                        validate: (value) => {
                                            if (!value || value.trim() === '') {
                                                return true; // Allow empty, only validate format if provided
                                            }
                                            try {
                                                JSON.parse(value);
                                                return true;
                                            } catch (e) {
                                                return "Invalid JSON format";
                                            }
                                        }
                                    })}
                                    className={`form-control ${getValidClass(errors.hostContent, getValues('hostContent'), isDelete)}`}
                                    style={{ resize: 'vertical', minHeight: '200px' }}
                                    disabled={disabled}
                                    placeholder='{ "body": { "node": "<hostname>", ... } }'
                                    title={errors.hostContent?.message}
                                    data-lpignore={true}
                                />
                                <small className="text-danger">{errors.hostContent?.message}</small>
                                <small className="text-muted d-block">Use substitution variables like &lt;hostname&gt;, etc.</small>
                            </div>
                            <div className="w-100"/>

                            {/* Enabled */}
                            <div className="col-xs-12 col-md-3">
                                <label className="auth">Enabled</label>
                            </div>
                            <div className="col-xs-12 col-md-6 col-lg-3">
                                <div className='form-check form-switch'>
                                    <input
                                        type="checkbox"
                                        id="enable"
                                        {...register("enable")}
                                        disabled={disabled}
                                        className="form-check-input"
                                        defaultChecked={true}
                                        data-lpignore={true}
                                    />
                                </div>
                            </div>
                            <div className="w-100"/>

                            {/* Actions */}
                            <div className="col-12 actions">
                                <button onClick={returnFromForm} type="button" className="btn btn-secondary"><i
                                          className="fal fa-times-circle fa-fw fa-sm"/><span>Cancel</span></button>
                                {action.type === 'update' && <TestNowButton className="btn btn-info"/>}
                                <button type="submit" className={`btn btn-${action.variant}`}><i
                                    className={`${action.icon} fa-sm`}/><span>{action.title}</span></button>
                            </div>
                            <TestResultAlert className="col-12"/>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default Detail;
